const DYNAMSOFT_TRIAL_LICENSE = process.env.REACT_APP_DYNAMSOFT_LICENSE?.trim()
  || 'DLS2eyJoYW5kc2hha2VDb2RlIjoiMTA2MDQ5NTYwLU1UQTJNRFE1TlRZd0xYZGxZaTFVY21saGJGQnliMm8iLCJtYWluU2VydmVyVVJMIjoiaHR0cHM6Ly9tZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwib3JnYW5pemF0aW9uSUQiOiIxMDYwNDk1NjAiLCJzdGFuZGJ5U2VydmVyVVJMIjoiaHR0cHM6Ly9zZGxzLmR5bmFtc29mdG9ubGluZS5jb20vIiwiY2hlY2tDb2RlIjotMTM4MjUyODY5OX0=';
const DYNAMSOFT_RESOURCE_ROOT = 'https://cdn.jsdelivr.net/npm/';
const DYNAMSOFT_SCRIPT_URL = `${DYNAMSOFT_RESOURCE_ROOT}dynamsoft-barcode-reader-bundle@11.4.3000/dist/dbr.bundle.js`;
const DYNAMSOFT_SCRIPT_ATTRIBUTE = 'data-code128-dynamsoft-sdk';
const CAPTURE_TEMPLATE = 'ReadBarcodes_ReadRateFirst';
const SDK_DOWNLOAD_TIMEOUT_MS = 20_000;
const ENGINE_LOAD_TIMEOUT_MS = 45_000;

interface DynamsoftBarcodeItem {
  angle?: number;
  confidence?: number;
  formatString?: string;
  moduleSize?: number;
  text?: string;
}

interface DynamsoftCapturedResult {
  decodedBarcodesResult?: {
    barcodeResultItems?: DynamsoftBarcodeItem[];
  };
  errorCode?: number;
  errorString?: string;
}

interface DynamsoftCamera {
  close: () => Promise<void>;
  dispose?: () => void;
  enableEnhancedFeatures: (features: number) => void;
  getFocusSettings: () => { mode?: string };
  getResolution: () => { height: number; width: number };
  getSelectedCamera: () => { label?: string } | undefined;
  getVideoEl?: () => HTMLVideoElement;
  open: () => Promise<void>;
  requestCamera: (constraints: MediaTrackConstraints) => Promise<void>;
  requestResolution: (constraints: MediaTrackConstraints) => Promise<void>;
}

interface DynamsoftCameraView {
  dispose?: () => void;
  getVideoElement?: () => HTMLVideoElement;
  getUIElement: () => HTMLElement;
  setVideoFit?: (fit: 'contain' | 'cover' | 'fill') => void;
}

interface DynamsoftRouter {
  addResultFilter: (filter: DynamsoftResultFilter) => Promise<void>;
  addResultReceiver: (receiver: DynamsoftResultReceiver) => Promise<void>;
  capture: (
    image: HTMLVideoElement | HTMLCanvasElement,
    template?: string,
  ) => Promise<DynamsoftCapturedResult>;
  dispose: () => void;
  getSimplifiedSettings: (template: string) => Promise<{
    barcodeSettings: {
      barcodeFormatIds: unknown;
      expectedBarcodesCount: number;
    };
  }>;
  maxImageSideLength: number;
  onCaptureError?: (error: unknown) => void;
  setInput: (camera: DynamsoftCamera) => void;
  startCapturing: (template: string) => Promise<void>;
  stopCapturing: () => void;
  updateSettings: (template: string, settings: unknown) => Promise<void>;
}

interface DynamsoftResultFilter {
  enableResultCrossVerification: (type: string, enabled: boolean) => void;
  enableResultDeduplication: (type: string, enabled: boolean) => void;
  setDuplicateForgetTime: (type: string, milliseconds: number) => void;
}

interface DynamsoftResultReceiver {
  onCapturedResultReceived: () => void;
  onDecodedBarcodesReceived: (result: {
    barcodeResultItems?: DynamsoftBarcodeItem[];
  }) => void;
}

interface DynamsoftSdk {
  Core: {
    CoreModule: {
      engineResourcePaths: { rootDirectory: string };
      loadWasm: () => Promise<void>;
      onWasmLoadProgressChanged?: (
        filePath: string,
        tag: string,
        progress: { loaded: number; total: number },
      ) => void;
    };
  };
  CVR: {
    CaptureVisionRouter: {
      createInstance: () => Promise<DynamsoftRouter>;
    };
  };
  DBR: {
    EnumBarcodeFormat: {
      BF_CODE_128: unknown;
      BF_QR_CODE: unknown;
    };
  };
  DCE: {
    CameraEnhancer: {
      createInstance: (view: DynamsoftCameraView) => Promise<DynamsoftCamera>;
    };
    CameraView: {
      createInstance: () => Promise<DynamsoftCameraView>;
    };
    EnumEnhancedFeatures: {
      EF_AUTO_ZOOM: number;
      EF_ENHANCED_FOCUS: number;
      EF_TAP_TO_FOCUS: number;
    };
  };
  License: {
    LicenseManager: {
      initLicense: (
        license: string,
        options?: { executeNow: boolean },
      ) => Promise<void> | void;
    };
  };
  Utility: {
    MultiFrameResultCrossFilter: new () => DynamsoftResultFilter;
  };
}

declare global {
  interface Window {
    Dynamsoft?: DynamsoftSdk;
  }
}

const FIRST_FRAME_TIMEOUT_MS = 2000;
const REPLAY_FRAME_TIMEOUT_MS = 800;
const REOPEN_FRAME_TIMEOUT_MS = 2500;
const FRAME_POLL_INTERVAL_MS = 120;
const CAMERA_WIDTH = 1920;
const CAMERA_HEIGHT = 1080;
const RECOVERY_CAMERA_WIDTH = 1280;
const RECOVERY_CAMERA_HEIGHT = 720;
const MANUAL_CAPTURE_INTERVAL_MS = 220;

let sdkLoadPromise: Promise<DynamsoftSdk> | null = null;
let engineLoadPromise: Promise<void> | null = null;

export interface Code128DiagnosticResult {
  angle: number;
  confidence: number;
  format: string;
  moduleSize: number;
  text: string;
}

export interface Code128DiagnosticCallbacks {
  onAttempt: (attempts: number) => void;
  onError: (message: string) => void;
  onLoading: (message: string, progress?: number) => void;
  onReady: (details: {
    camera: string;
    focus: string;
    resolution: string;
  }) => void;
  onResult: (result: Code128DiagnosticResult) => void;
}

export interface Code128DiagnosticSession {
  stop: () => Promise<void>;
  video: HTMLVideoElement;
}

type DynamsoftBarcodeMode = 'code128' | 'qr';

function getErrorMessage(error: unknown) {
  return error instanceof Error ? error.message : String(error);
}

function getCameraVideoElement(
  cameraEnhancer: DynamsoftCamera,
  cameraView: DynamsoftCameraView,
) {
  const enhancerVideo = cameraEnhancer.getVideoEl?.();
  if (enhancerVideo instanceof HTMLVideoElement) {
    return enhancerVideo;
  }

  const viewVideo = cameraView.getVideoElement?.();
  if (viewVideo instanceof HTMLVideoElement) {
    return viewVideo;
  }

  const domVideo = cameraView.getUIElement().querySelector('video');
  if (domVideo instanceof HTMLVideoElement) {
    return domVideo;
  }

  throw new Error('Code128 摄像头已启动，但当前 SDK 未暴露可共享的视频画面');
}

function prepareCameraVideo(video: HTMLVideoElement) {
  video.autoplay = true;
  video.muted = true;
  video.playsInline = true;
  video.setAttribute('autoplay', '');
  video.setAttribute('muted', '');
  video.setAttribute('playsinline', '');
}

function waitForVideoFrames(video: HTMLVideoElement, timeoutMs: number) {
  return new Promise<boolean>((resolve) => {
    const events = ['canplay', 'loadeddata', 'playing', 'resize'];
    const startTime = video.currentTime;
    const frameVideo = video as HTMLVideoElement & {
      cancelVideoFrameCallback?: (handle: number) => void;
      requestVideoFrameCallback?: (callback: () => void) => number;
    };
    let deliveredFrame = false;
    let frameCallback = 0;
    let poll = 0;
    let timer = 0;

    function check() {
      const hasDimensions = video.videoWidth > 0 && video.videoHeight > 0 && !video.paused;
      const playbackAdvanced = video.currentTime > startTime + 0.001;
      if (hasDimensions && (deliveredFrame || playbackAdvanced)) {
        finish(true);
      }
    }

    function finish(ready: boolean) {
      if (frameCallback && frameVideo.cancelVideoFrameCallback) {
        frameVideo.cancelVideoFrameCallback(frameCallback);
      }
      window.clearInterval(poll);
      window.clearTimeout(timer);
      events.forEach((name) => video.removeEventListener(name, check));
      resolve(ready);
    }

    if (frameVideo.requestVideoFrameCallback) {
      frameCallback = frameVideo.requestVideoFrameCallback(() => {
        deliveredFrame = true;
        check();
      });
    }
    events.forEach((name) => video.addEventListener(name, check));
    // 部分 Android WebView 不会把这些事件补发给后挂的监听器，再加一路轮询兜底。
    poll = window.setInterval(check, FRAME_POLL_INTERVAL_MS);
    timer = window.setTimeout(() => {
      const hasDimensions = video.videoWidth > 0 && video.videoHeight > 0 && !video.paused;
      finish(hasDimensions && (deliveredFrame || video.currentTime > startTime + 0.001));
    }, timeoutMs);
  });
}

async function playCameraVideo(video: HTMLVideoElement) {
  prepareCameraVideo(video);
  try {
    await video.play();
  } catch {
    // Android WebView 可能要等摄像头 open 完成后才能起播，后续仍会轮询首帧。
  }
}

// 摄像头「打开了但没有画面」的兜底。
//
// 现象是首次点「允许」后预览全黑，把面板关掉重开就正常。此时 open() 已经 resolve、
// video 元素也挂上了、容器 CSS 带 min-width/min-height 所以不是尺寸为 0 —— 就是一帧
// 都没来：权限弹框打断了这一轮的取流/起播，而 SDK 自己不重试。
//
// 与其继续猜是哪一环被打断，不如把用户手动「关掉重开」那一步替他做掉 —— 那一步是
// 已知有效的：那时权限已授予，重新枚举拿得到真实设备，起播也不会再被弹框打断。
async function ensureCameraFrames(
  cameraEnhancer: DynamsoftCamera,
  cameraView: DynamsoftCameraView,
  onLoading: Code128DiagnosticCallbacks['onLoading'],
) {
  const video = getCameraVideoElement(cameraEnhancer, cameraView);
  await playCameraVideo(video);
  if (await waitForVideoFrames(video, FIRST_FRAME_TIMEOUT_MS)) {
    return video;
  }

  // 弹框期间页面不可见，自动播放可能被判成非用户触发而没起播，先补一次 play()。
  await playCameraVideo(video);
  if (await waitForVideoFrames(video, REPLAY_FRAME_TIMEOUT_MS)) {
    return video;
  }

  onLoading('摄像头没有画面，正在用 PDA 兼容模式重新打开…');
  await cameraEnhancer.close();
  await cameraEnhancer.requestResolution({
    height: { ideal: RECOVERY_CAMERA_HEIGHT },
    width: { ideal: RECOVERY_CAMERA_WIDTH },
  });
  await cameraEnhancer.open();
  const reopenedVideo = getCameraVideoElement(cameraEnhancer, cameraView);
  await playCameraVideo(reopenedVideo);
  if (await waitForVideoFrames(reopenedVideo, REOPEN_FRAME_TIMEOUT_MS)) {
    return reopenedVideo;
  }

  // 重开还是黑的就别硬撑：抛出去让调用方退到普通 getUserMedia 预览。60×60 条码识别
  // 没了，但摄像头至少是能看见的，80×80 还能扫 —— 好过给操作员一个黑框。
  throw new Error('摄像头已打开但始终没有画面');
}

function getLoadedSdk() {
  const sdk = window.Dynamsoft;
  if (!sdk?.Core?.CoreModule || !sdk.CVR?.CaptureVisionRouter) {
    throw new Error('Code128 SDK 脚本已加载，但浏览器未找到识别引擎');
  }
  return sdk;
}

function loadDynamsoftSdk(): Promise<DynamsoftSdk> {
  if (window.Dynamsoft) {
    return Promise.resolve(getLoadedSdk());
  }
  if (sdkLoadPromise) {
    return sdkLoadPromise;
  }

  sdkLoadPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[${DYNAMSOFT_SCRIPT_ATTRIBUTE}]`,
    );
    const script = existingScript || document.createElement('script');
    const timeout = window.setTimeout(() => {
      sdkLoadPromise = null;
      script.remove();
      reject(new Error('Code128 SDK 下载超过 20 秒，请切换手机网络后重试'));
    }, SDK_DOWNLOAD_TIMEOUT_MS);

    const handleLoad = () => {
      window.clearTimeout(timeout);
      try {
        resolve(getLoadedSdk());
      } catch (error) {
        sdkLoadPromise = null;
        script.remove();
        reject(error);
      }
    };
    const handleError = () => {
      window.clearTimeout(timeout);
      sdkLoadPromise = null;
      script.remove();
      reject(new Error('Code128 SDK 下载失败，请确认手机已联网后重试'));
    };

    script.addEventListener('load', handleLoad, { once: true });
    script.addEventListener('error', handleError, { once: true });
    if (!existingScript) {
      script.src = DYNAMSOFT_SCRIPT_URL;
      script.async = true;
      script.crossOrigin = 'anonymous';
      script.setAttribute(DYNAMSOFT_SCRIPT_ATTRIBUTE, 'true');
      document.head.appendChild(script);
    }
  });

  return sdkLoadPromise;
}

function withTimeout<T>(promise: Promise<T>, milliseconds: number, message: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => reject(new Error(message)), milliseconds);
    promise.then(
      (value) => {
        window.clearTimeout(timeout);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timeout);
        reject(error);
      },
    );
  });
}

async function loadDynamsoftEngine(
  sdk: DynamsoftSdk,
  onLoading?: (message: string, progress?: number) => void,
) {
  sdk.Core.CoreModule.engineResourcePaths.rootDirectory = DYNAMSOFT_RESOURCE_ROOT;
  sdk.Core.CoreModule.onWasmLoadProgressChanged = (_filePath, _tag, progress) => {
    const percent = progress.total > 0
      ? Math.min(100, Math.round((progress.loaded / progress.total) * 100))
      : 0;
    onLoading?.(`正在加载条形码识别引擎 ${percent}%`, percent);
  };

  if (!engineLoadPromise) {
    engineLoadPromise = (async () => {
      await Promise.resolve(
        sdk.License.LicenseManager.initLicense(DYNAMSOFT_TRIAL_LICENSE, { executeNow: true }),
      );
      await sdk.Core.CoreModule.loadWasm();
    })().catch((error) => {
      engineLoadPromise = null;
      throw error;
    });
  }
  await withTimeout(
    engineLoadPromise,
    ENGINE_LOAD_TIMEOUT_MS,
    'Code128 识别引擎加载超过 45 秒，可能被当前网络拦截，请切换网络后重试',
  );
}

function assertSecureContext() {
  const isLocalhost = ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  if (!window.isSecureContext && !isLocalhost) {
    throw new Error('第三方测试许可证要求使用 HTTPS；请通过 HTTPS 地址打开本页面');
  }
}

async function prepareDynamsoftCode128(
  onLoading?: (message: string, progress?: number) => void,
) {
  assertSecureContext();
  onLoading?.('正在下载 Code128 识别 SDK…', 0);
  const sdk = await loadDynamsoftSdk();
  onLoading?.('SDK 已下载，正在加载条形码识别引擎…', 0);
  await loadDynamsoftEngine(sdk, onLoading);
  return sdk;
}

export async function preloadDynamsoftCode128() {
  await prepareDynamsoftCode128();
}

async function createConfiguredRouter(sdk: DynamsoftSdk, mode: DynamsoftBarcodeMode) {
  const router = await sdk.CVR.CaptureVisionRouter.createInstance();
  router.maxImageSideLength = CAMERA_WIDTH;
  const settings = await router.getSimplifiedSettings(CAPTURE_TEMPLATE);
  settings.barcodeSettings.barcodeFormatIds = mode === 'qr'
    ? sdk.DBR.EnumBarcodeFormat.BF_QR_CODE
    : sdk.DBR.EnumBarcodeFormat.BF_CODE_128;
  settings.barcodeSettings.expectedBarcodesCount = 1;
  await router.updateSettings(CAPTURE_TEMPLATE, settings);
  return router;
}

async function startDynamsoftBarcodeDiagnostic(
  container: HTMLElement,
  callbacks: Code128DiagnosticCallbacks,
  mode: DynamsoftBarcodeMode,
): Promise<Code128DiagnosticSession> {
  const Dynamsoft = await prepareDynamsoftCode128(callbacks.onLoading);
  callbacks.onLoading('识别引擎已加载，正在打开后置摄像头…', 100);

  const router = await createConfiguredRouter(Dynamsoft, mode);
  const cameraView = await Dynamsoft.DCE.CameraView.createInstance();
  cameraView.setVideoFit?.('cover');
  const cameraEnhancer = await Dynamsoft.DCE.CameraEnhancer.createInstance(cameraView);
  let stopped = false;
  let attempts = 0;
  let manualCapture = false;
  let manualCaptureTimer = 0;
  let sharedVideo: HTMLVideoElement | null = null;

  const submitBarcodeItems = (items?: DynamsoftBarcodeItem[]) => {
    items?.forEach((item) => {
      const text = item.text?.trim() || '';
      if (!text) {
        return;
      }
      callbacks.onResult({
        angle: item.angle || 0,
        confidence: item.confidence || 0,
        format: item.formatString || 'Code 128',
        moduleSize: item.moduleSize || 0,
        text,
      });
    });
  };

  const scheduleManualCapture = (delay = MANUAL_CAPTURE_INTERVAL_MS) => {
    window.clearTimeout(manualCaptureTimer);
    if (stopped || !manualCapture) {
      return;
    }
    manualCaptureTimer = window.setTimeout(() => {
      void captureManualFrame();
    }, delay);
  };

  const captureManualFrame = async () => {
    if (stopped || !manualCapture || !sharedVideo) {
      return;
    }
    try {
      attempts += 1;
      callbacks.onAttempt(attempts);
      const result = await router.capture(sharedVideo, CAPTURE_TEMPLATE);
      if (result.errorCode && result.errorCode !== 0) {
        throw new Error(result.errorString || `识别失败 (${result.errorCode})`);
      }
      submitBarcodeItems(result.decodedBarcodesResult?.barcodeResultItems);
      scheduleManualCapture();
    } catch (error) {
      if (!stopped) {
        callbacks.onError(`PDA 兼容识别异常：${getErrorMessage(error)}`);
        scheduleManualCapture(500);
      }
    }
  };

  const startManualCapture = () => {
    if (stopped || manualCapture || !sharedVideo) {
      return;
    }
    manualCapture = true;
    router.stopCapturing();
    callbacks.onLoading('已切换为 PDA 兼容识别模式');
    scheduleManualCapture(0);
  };

  const stop = async () => {
    if (stopped) {
      return;
    }
    stopped = true;
    window.clearTimeout(manualCaptureTimer);
    router.stopCapturing();
    await cameraEnhancer.close();
    router.dispose();
    cameraEnhancer.dispose?.();
    cameraView.dispose?.();
    container.replaceChildren();
  };

  try {
    container.replaceChildren(cameraView.getUIElement());
    router.setInput(cameraEnhancer);
    // 部分旧 Android WebView 能正常预览，却不支持 SDK 的连续 CameraEnhancer
    // 输入，会异步报 [capture] 22566424。此时保留同一视频，改用官方支持的
    // router.capture(HTMLVideoElement) 逐帧识别，避免摄像头看得见却永远是 0 帧。
    router.onCaptureError = () => startManualCapture();

    const receiver: DynamsoftResultReceiver = {
      onCapturedResultReceived: () => {
        attempts += 1;
        callbacks.onAttempt(attempts);
      },
      onDecodedBarcodesReceived: (result) => {
        submitBarcodeItems(result.barcodeResultItems);
      },
    };
    await router.addResultReceiver(receiver);

    const filter = new Dynamsoft.Utility.MultiFrameResultCrossFilter();
    filter.enableResultCrossVerification('barcode', true);
    filter.enableResultDeduplication('barcode', true);
    filter.setDuplicateForgetTime('barcode', 1500);
    await router.addResultFilter(filter);

    await cameraEnhancer.requestCamera({ facingMode: { ideal: 'environment' } });
    await cameraEnhancer.requestResolution({
      height: { ideal: CAMERA_HEIGHT },
      width: { ideal: CAMERA_WIDTH },
    });
    await cameraEnhancer.open();
    const video = await ensureCameraFrames(cameraEnhancer, cameraView, callbacks.onLoading);
    sharedVideo = video;

    const resolution = cameraEnhancer.getResolution();
    const camera = cameraEnhancer.getSelectedCamera();
    let focus = '浏览器未提供';
    try {
      focus = cameraEnhancer.getFocusSettings().mode || focus;
    } catch {
      // Some mobile browsers do not expose focus information.
    }
    callbacks.onReady({
      camera: camera?.label || '后置摄像头',
      focus,
      resolution: `${resolution.width}×${resolution.height}`,
    });
    await router.startCapturing(CAPTURE_TEMPLATE);
    return {
      stop,
      video,
    };
  } catch (error) {
    await stop();
    throw error;
  }
}

export async function startDynamsoftCode128Diagnostic(
  container: HTMLElement,
  callbacks: Code128DiagnosticCallbacks,
) {
  return startDynamsoftBarcodeDiagnostic(container, callbacks, 'code128');
}

export async function startDynamsoftQrDiagnostic(
  container: HTMLElement,
  callbacks: Code128DiagnosticCallbacks,
) {
  return startDynamsoftBarcodeDiagnostic(container, callbacks, 'qr');
}
